import { useState, useEffect } from 'react';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { useAuth } from '@/lib/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { 
  KanbanSquare, LineChart, ShieldCheck, Zap, 
  Smartphone, CreditCard, ChevronRight, CheckCircle2, Play,
  Camera, ShieldAlert, Sparkles, MessageSquare
} from 'lucide-react';

import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

type Plan = {
  id: string;
  name: string;
  description: string;
  price: number;
  features_json: string[];
};

const defaultPlans: Plan[] = [
  { id: 'basico', name: 'Básico', description: 'Para lava-rápidos que estão profissionalizando a gestão.', price: 97, features_json: ['Painel Kanban de OS', 'Controle de Caixa Simples', 'Cadastro de Clientes', 'Suporte via Email'] },
  { id: 'pro', name: 'Pro', description: 'Para estéticas automotivas de alto fluxo e exigência.', price: 147, features_json: ['Tudo do plano Básico', 'Leitura OCR de Placa', 'Caixa Cego (Antifraude)', 'Módulo de Comissões', 'Suporte WhatsApp'] },
  { id: 'premium', name: 'Premium', description: 'Para redes e franquias que tomam decisões baseadas em dados.', price: 297, features_json: ['Tudo do plano Pro', 'AI Growth Dashboard', 'Pesquisas NPS', 'Gerente de Conta Dedicado'] }
];

function LandingPage() {
  useDocumentMeta({
    title: 'AutoWash Pro - Gestão Inteligente para Estética Automotiva',
    description: 'O sistema premium de gestão para lava-rápidos e estéticas automotivas. Controle pátio, caixa, clientes e equipe em tempo real com OCR de placa e IA.',
  });

  const { user, isLoading, hasOnboarded } = useAuth();
  const [plans, setPlans] = useState<Plan[]>(defaultPlans);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    async function loadPlans() {
      const { data, error } = await supabase
        .from('system_plans')
        .select('id, name, description, price, features_json')
        .eq('status', 'ACTIVE')
        .order('price', { ascending: true });
      if (data && !error && data.length > 0) {
        setPlans(data as Plan[]);
      }
    }
    loadPlans();
  }, []);

  // Se o usuário logado acessar a raiz, mandamos pro app ou pro master.
  if (!isLoading && user && hasOnboarded !== null) {
    if (user.user_metadata?.role === 'SUPERADMIN') {
      return <Navigate to="/master" />;
    }
    if (hasOnboarded === false) return <Navigate to="/onboarding" />;
    return <Navigate to="/app" />;
  }

  // Preço anual com 20% de desconto
  const getDisplayPrice = (basePrice: number) => {
    if (billingPeriod === 'yearly') {
      return Math.round(basePrice * 0.8);
    }
    return basePrice;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 font-sans selection:bg-blue-500/30 overflow-x-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-slate-950/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="AutoWash Pro Logo" className="w-10 h-10 object-contain" />
            <span className="font-bold text-xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
              AutoWash Pro
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
            <a href="#recursos" className="hover:text-white transition-colors">Recursos</a>
            <a href="#solucao" className="hover:text-white transition-colors">Solução</a>
            <a href="#precos" className="hover:text-white transition-colors">Preços</a>
          </div>

          <div className="flex items-center gap-4">
            <a href="/login?trial=true">
              <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold hidden sm:flex">
                Teste Grátis (7 Dias)
              </Button>
            </a>
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white hidden sm:flex">
                Entrar
              </Button>
            </Link>
            <Link to="/sandbox">
              <Button className="bg-white text-slate-900 hover:bg-slate-200 font-semibold shadow-xl shadow-white/10 rounded-full px-6">
                Ver Demonstração
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 px-6">
        {/* Aurora Glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[550px] bg-blue-600/10 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[400px] bg-indigo-600/10 blur-[130px] rounded-full pointer-events-none" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 font-medium text-sm mb-8">
            <Sparkles className="w-4 h-4 text-blue-400 animate-pulse" />
            Nova Versão 2.0 Lançada com Leitura OCR de Placas
          </div>
          
          <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight mb-8 leading-tight">
            Gestão de Estética Automotiva <br className="hidden lg:block" />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">
              que Impulsiona Lucros.
            </span>
          </h1>
          
          <p className="text-lg lg:text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed">
            Abandone o papel e as planilhas complexas. Controle o fluxo do pátio com Kanban, feche o caixa com segurança, identifique carros com câmera OCR e receba insights de IA.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/sandbox">
              <Button size="lg" className="h-14 px-8 text-base bg-blue-600 hover:bg-blue-500 text-white rounded-full font-semibold shadow-lg shadow-blue-500/25 w-full sm:w-auto group">
                <Play className="w-5 h-5 mr-2 fill-current opacity-80 group-hover:opacity-100" />
                Ver Demonstração
              </Button>
            </Link>
            <a href="#precos">
              <Button size="lg" className="h-14 px-8 text-base bg-slate-900 border border-slate-700 hover:bg-slate-800 text-white rounded-full w-full sm:w-auto">
                Ver Planos
              </Button>
            </a>
          </div>
        </div>

        {/* Dashboard Premium Mockup (Mac Window) */}
        <div className="max-w-5xl mx-auto mt-20 relative">
          <div className="absolute -inset-1.5 bg-gradient-to-tr from-blue-500/30 to-purple-500/30 rounded-2xl blur opacity-35"></div>
          
          <div className="relative bg-slate-950/80 border border-white/10 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
            {/* Mac Top Chrome */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-slate-900/50">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <div className="text-xs font-mono text-slate-500 bg-slate-950 px-6 py-1 rounded-md border border-white/5">
                autowash.pro/app/patio
              </div>
              <div className="w-12" />
            </div>

            {/* Mockup UI Workspace */}
            <div className="flex min-h-[400px]">
              {/* Sidebar */}
              <div className="w-48 bg-slate-900/40 border-r border-white/5 hidden md:flex flex-col p-4 gap-3">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded bg-blue-500 flex items-center justify-center font-bold text-xs text-slate-950">AP</div>
                  <span className="text-xs font-bold text-slate-300">AutoWash</span>
                </div>
                {[
                  { name: 'Pátio & Kanban', active: true },
                  { name: 'Caixas', active: false },
                  { name: 'Clientes & Carros', active: false },
                  { name: 'Comissões', active: false },
                  { name: 'Growth Módulo', active: false }
                ].map((item, idx) => (
                  <div key={idx} className={`h-8 px-3 rounded flex items-center text-xs font-medium ${item.active ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'text-slate-400 hover:text-slate-200'}`}>
                    {item.name}
                  </div>
                ))}
              </div>

              {/* Main Content Pane */}
              <div className="flex-1 p-6 bg-slate-900/10">
                <div className="flex justify-between items-center mb-6">
                  <div className="h-6 w-32 bg-slate-800 rounded"></div>
                  <div className="flex gap-2">
                    <div className="h-6 w-16 bg-blue-500/10 border border-blue-500/20 rounded text-[10px] text-blue-400 flex items-center justify-center font-medium">ONLINE</div>
                    <div className="h-6 w-6 rounded-full bg-slate-800"></div>
                  </div>
                </div>

                {/* Mockup KPIs */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {[
                    { label: 'Hoje na Fila', value: '14' },
                    { label: 'Faturamento Hoje', value: 'R$ 1.840,00' },
                    { label: 'Finalizados', value: '8' }
                  ].map((card, idx) => (
                    <div key={idx} className="bg-slate-900/50 border border-white/5 rounded-xl p-4">
                      <span className="text-[10px] text-slate-500 block mb-1 uppercase font-semibold">{card.label}</span>
                      <span className="text-lg font-bold text-white">{card.value}</span>
                    </div>
                  ))}
                </div>

                {/* Kanban Simulation */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { title: 'Aguardando (5)', items: ['Civic - Lavagem Simples', 'Compass - Polimento'] },
                    { title: 'Em Execução (2)', items: ['Corolla - Destaque OCR'] },
                    { title: 'Pronto (7)', items: ['T-Cross - Higienização'] }
                  ].map((col, idx) => (
                    <div key={idx} className="bg-slate-950/40 border border-white/5 rounded-xl p-3 min-h-[160px]">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-xs font-bold text-slate-400">{col.title}</span>
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      </div>
                      <div className="space-y-2">
                        {col.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="bg-slate-900 border border-white/5 rounded p-2 text-[11px] text-slate-300 font-medium shadow-sm">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Overlay Gradient to fade out the bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none"></div>
          </div>
        </div>
      </section>

      {/* Bento Grid: Features Grid Section */}
      <section id="recursos" className="py-24 bg-slate-950 px-6 border-t border-white/5 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">Projetado para Produtividade</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Tecnologia avançada combinada com usabilidade fluida para transformar a rotina do seu pátio.</p>
          </div>

          {/* Bento Grid Container */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* CARD 1: KANBAN FLOW (Destaque - 2 Colunas) */}
            <div className="md:col-span-2 bg-gradient-to-tr from-slate-900/60 to-slate-900 border border-white/5 p-8 rounded-3xl relative overflow-hidden group hover:border-blue-500/30 transition-all duration-300">
              <div className="absolute -right-10 -bottom-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl pointer-events-none"></div>
              
              <div className="flex flex-col h-full justify-between relative z-10">
                <div>
                  <div className="w-12 h-12 bg-blue-500/10 text-blue-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                    <KanbanSquare className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Fluxo Kanban em Tempo Real</h3>
                  <p className="text-slate-400 max-w-lg leading-relaxed mb-6">
                    Acompanhe visualmente a entrada, a execução e a finalização de cada serviço. Reduza gargalos no pátio e organize a equipe com simples arrastar e soltar de cards de serviço.
                  </p>
                </div>
                
                {/* Mini Kanban Preview */}
                <div className="mt-4 bg-slate-950/60 border border-white/5 rounded-2xl p-4 grid grid-cols-3 gap-3">
                  <div className="bg-slate-900/40 rounded-xl p-2.5">
                    <span className="text-[10px] text-slate-500 font-bold block mb-2">AGUARDANDO</span>
                    <div className="bg-slate-900 border border-white/5 p-2 rounded text-[10px] text-slate-300 shadow">Honda HR-V</div>
                  </div>
                  <div className="bg-slate-900/40 rounded-xl p-2.5">
                    <span className="text-[10px] text-blue-400 font-bold block mb-2">LAVAGEM</span>
                    <div className="bg-slate-900 border border-blue-500/30 p-2 rounded text-[10px] text-white shadow-md relative">
                      Audi Q3
                      <div className="absolute right-1.5 top-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 animate-ping"></div>
                    </div>
                  </div>
                  <div className="bg-slate-900/40 rounded-xl p-2.5">
                    <span className="text-[10px] text-emerald-400 font-bold block mb-2">PRONTO</span>
                    <div className="bg-slate-900 border border-white/5 p-2 rounded text-[10px] text-slate-400 line-through">Pajero</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CARD 2: AI GROWTH DASHBOARD (1 Coluna) */}
            <div className="bg-gradient-to-b from-slate-900/60 to-slate-900 border border-white/5 p-8 rounded-3xl group hover:border-indigo-500/30 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                  <LineChart className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">AI Growth Dashboard</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Decisões baseadas em estatísticas. Gráficos de faturamento, ticket médio e serviços mais rentáveis calculados de forma automática por Inteligência Artificial.
                </p>
              </div>

              {/* mini insights preview */}
              <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                  <span className="text-[10px] font-bold text-slate-300">INSIGHT DA IA</span>
                </div>
                <p className="text-[10px] text-indigo-300 italic leading-snug">
                  "O serviço 'Polimento Técnico' gerou 42% do lucro bruto nos últimos 30 dias. Sugerimos campanha promocional para finais de semana."
                </p>
              </div>
            </div>

            {/* CARD 3: LEITURA OCR DE PLACA (Destaque - 2 Colunas) */}
            <div className="md:col-span-2 bg-gradient-to-tr from-slate-900/60 to-slate-900 border border-white/5 p-8 rounded-3xl relative overflow-hidden group hover:border-purple-500/30 transition-all duration-300">
              <div className="absolute -left-10 -bottom-10 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl pointer-events-none"></div>
              
              <div className="flex flex-col h-full justify-between relative z-10">
                <div>
                  <div className="w-12 h-12 bg-purple-500/10 text-purple-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                    <Camera className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Leitura OCR de Placas</h3>
                  <p className="text-slate-400 max-w-lg leading-relaxed mb-6">
                    Agilize o pátio e minimize erros de digitação. Ao apontar a câmera do celular para a placa do carro, o sistema faz o escaneamento OCR instantâneo, busca o cadastro do cliente e puxa os dados do veículo.
                  </p>
                </div>
                
                {/* Plate Scanning Mockup */}
                <div className="mt-4 bg-slate-950/80 border border-white/5 rounded-2xl p-4 flex items-center justify-between gap-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-slate-900 border-2 border-slate-700 px-3 py-1 rounded font-mono font-bold text-sm tracking-wider text-white shadow-sm flex items-center gap-1.5 relative overflow-hidden">
                      <div className="absolute top-0 bottom-0 left-0 w-1 bg-blue-500"></div>
                      BRA3E24
                      <div className="h-full w-0.5 bg-green-500 absolute animate-pulse right-2 top-0 bottom-0"></div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                    <div className="text-left">
                      <span className="text-[10px] text-slate-500 block font-semibold">VEÍCULO ENCONTRADO</span>
                      <span className="text-[11px] text-white font-bold">Toyota Corolla Altis (Preto)</span>
                    </div>
                  </div>
                  <div className="px-2.5 py-1 rounded bg-green-500/10 border border-green-500/20 text-[9px] text-green-400 font-bold uppercase tracking-wider">
                    OCR Identificado
                  </div>
                </div>
              </div>
            </div>

            {/* CARD 4: COMISSÕES AUTOMÁTICAS (1 Coluna) */}
            <div className="bg-gradient-to-b from-slate-900/60 to-slate-900 border border-white/5 p-8 rounded-3xl group hover:border-amber-500/30 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-amber-500/10 text-amber-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                  <Zap className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Comissões Automáticas</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Diga adeus às disputas por comissão no fim do dia. Distribua e calcule a comissão de cada funcionário no momento do checkout, inclusive em serviços compartilhados.
                </p>
              </div>

              {/* Commission List Preview */}
              <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-3.5 space-y-2">
                <div className="flex justify-between items-center text-[10px]">
                  <span className="text-slate-400">Marcos (Lavador)</span>
                  <span className="text-emerald-400 font-bold">+R$ 18,00</span>
                </div>
                <div className="w-full h-px bg-white/5"></div>
                <div className="flex justify-between items-center text-[10px]">
                  <span className="text-slate-400">Lucas (Vistoriador)</span>
                  <span className="text-emerald-400 font-bold">+R$ 7,50</span>
                </div>
              </div>
            </div>

            {/* CARD 5: CAIXA CEGO ANTIFRAUDE (1 Coluna) */}
            <div className="bg-gradient-to-b from-slate-900/60 to-slate-900 border border-white/5 p-8 rounded-3xl group hover:border-red-500/30 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Caixa Cego (Antifraude)</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Bloqueie desvios. O operador de caixa fecha a gaveta declarando os valores às cegas. Divergências em relação ao sistema geram alertas imediatos de auditoria.
                </p>
              </div>

              <div className="bg-slate-950/60 border border-red-500/10 rounded-2xl p-3.5 flex items-center gap-3">
                <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
                <div className="text-left">
                  <span className="text-[9px] text-red-400 font-bold block uppercase">Divergência Detectada</span>
                  <span className="text-[10px] text-slate-300">R$ -45,00 declarados a menor no Pix</span>
                </div>
              </div>
            </div>

            {/* CARD 6: CHECKOUT & CUPONS (1 Coluna) */}
            <div className="bg-gradient-to-b from-slate-900/60 to-slate-900 border border-white/5 p-8 rounded-3xl group hover:border-emerald-500/30 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-emerald-500/10 text-emerald-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                  <CreditCard className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Checkout e Cupons</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Gerencie cupons de desconto, registre pagamentos múltiplos, envie comprovantes térmicos e acompanhe as taxas das adquirentes direto nas transações.
                </p>
              </div>

              <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-3.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-emerald-400" />
                  <span className="text-[10px] text-slate-300">Enviar Recibo PDF</span>
                </div>
                <span className="text-[9px] bg-emerald-500/10 text-emerald-400 font-bold px-2 py-0.5 rounded border border-emerald-500/20">Via WhatsApp</span>
              </div>
            </div>

            {/* CARD 7: RESPONSIVIDADE TOTAL (1 Coluna) */}
            <div className="bg-gradient-to-b from-slate-900/60 to-slate-900 border border-white/5 p-8 rounded-3xl group hover:border-slate-400 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-slate-100/10 text-slate-300 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                  <Smartphone className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">100% Responsivo</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Acesse pelo computador da administração, pelo tablet da recepção ou pelo celular do vistoriador direto na rampa de lavagem.
                </p>
              </div>

              <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-3 flex justify-around text-[10px] text-slate-400 font-semibold">
                <span>🖥️ Desktop</span>
                <span>📱 Mobile</span>
                <span>📟 Tablet</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Split Layout: Dores vs Soluções */}
      <section id="solucao" className="py-24 bg-slate-900/30 px-6 border-y border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">O caos no pátio custa dinheiro todos os dias.</h2>
            <p className="text-slate-400 text-lg mb-8">
              Perder o controle de qual carro chegou primeiro, esquecer de cobrar um serviço adicional ou fechar o caixa no negativo. Se isso acontece, você está deixando dinheiro na mesa.
            </p>
            <div className="space-y-4">
              {[
                "Papéis perdidos ou molhados no pátio",
                "Desorganização na fila de lavagem",
                "Erros no cálculo de comissões no fim do dia"
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-300 bg-slate-950/60 border border-white/5 p-4 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-red-500/10 text-red-400 flex items-center justify-center shrink-0">✕</div>
                  {text}
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500/5 blur-3xl rounded-full"></div>
            <div className="relative bg-slate-900/60 border border-white/10 p-8 rounded-2xl shadow-xl backdrop-blur-md">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <CheckCircle2 className="text-emerald-400" /> O Padrão AutoWash Pro
              </h3>
              <div className="space-y-6">
                <SolutionItem 
                  title="Ordem de Serviço Digital" 
                  desc="Crie OS completas em 10 segundos com cálculo automático." 
                />
                <SolutionItem 
                  title="Leitura Inteligente (OCR)" 
                  desc="Carregue dados do carro e cliente automaticamente pela câmera." 
                />
                <SolutionItem 
                  title="Organização Visual" 
                  desc="O pátio inteiro em uma tela. Saiba exatamente o que a equipe está fazendo." 
                />
                <SolutionItem 
                  title="Controle Absoluto de Caixa" 
                  desc="Transações financeiras vinculadas à OS com auditoria implacável." 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="py-24 bg-slate-950 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">Planos Simples e Transparentes</h2>
            <p className="text-slate-400 text-lg">Sem taxas extras ocultas. Escolha o faturamento ideal.</p>
            
            {/* Billing Period Toggle Switch */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-white' : 'text-slate-500'}`}>Mensal</span>
              <button 
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                className="w-12 h-6 rounded-full bg-blue-600/30 border border-blue-500/50 p-1 flex items-center transition-all duration-300"
              >
                <div className={`w-4 h-4 rounded-full bg-blue-400 transition-transform duration-300 ${billingPeriod === 'yearly' ? 'translate-x-6 bg-indigo-400' : ''}`} />
              </button>
              <div className="flex items-center gap-1.5">
                <span className={`text-sm font-medium ${billingPeriod === 'yearly' ? 'text-white' : 'text-slate-500'}`}>Anual</span>
                <span className="bg-indigo-500/10 text-indigo-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-indigo-500/20">Economize 20%</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-16">
            {plans.map((plan, index) => {
              const isHighlight = index === 2; // Destacar o plano Premium
              const displayPrice = getDisplayPrice(plan.price);
              
              if (isHighlight) {
                return (
                  <div key={plan.id} className="bg-gradient-to-b from-purple-950/40 to-slate-950 border border-purple-500/30 rounded-3xl p-8 flex flex-col relative overflow-hidden transform md:-translate-y-4 shadow-2xl shadow-purple-900/10">
                    <div className="absolute top-0 right-0 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      RECOMENDADO
                    </div>
                    <h3 className="text-2xl font-bold text-purple-400 mb-2">{plan.name}</h3>
                    <p className="text-slate-400 text-sm mb-6">{plan.description}</p>
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-extrabold text-white">R$ {displayPrice}</span>
                        <span className="text-slate-500">/mês</span>
                      </div>
                      <p className="text-emerald-400 text-sm font-medium mt-1">
                        ou R$ {(displayPrice * 0.9).toFixed(2).replace('.', ',')} no PIX (10% off)
                      </p>
                      {billingPeriod === 'yearly' && (
                        <span className="text-[10px] text-slate-500 block mt-1">Cobrado anualmente</span>
                      )}
                    </div>
                    <div className="space-y-3 mb-8 flex-1">
                      {plan.features_json.map((feature, i) => (
                        <PricingFeature key={i} text={feature} />
                      ))}
                    </div>
                    <a href={`/login?plan=${plan.id}`} className="w-full">
                      <Button className="w-full bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-500/25 rounded-xl h-12">
                        Assinar {plan.name}
                      </Button>
                    </a>
                    <a href="/login?trial=true" className="w-full block mt-3">
                      <Button variant="outline" className="w-full rounded-xl h-12 bg-transparent text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-white">
                        Iniciar Teste Grátis (7 dias)
                      </Button>
                    </a>
                  </div>
                );
              }

              // Estilo Premium (roxo) ou Básico (escuro)
              const isPremium = index === 2;
              
              return (
                <div key={plan.id} className="bg-slate-900/30 border border-white/5 rounded-3xl p-8 flex flex-col">
                  <h3 className={`text-2xl font-bold mb-2 ${isPremium ? 'text-purple-400' : 'text-white'}`}>
                    {plan.name}
                  </h3>
                  <p className="text-slate-400 text-sm mb-6">{plan.description}</p>
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-white">R$ {displayPrice}</span>
                      <span className="text-slate-500">/mês</span>
                    </div>
                    <p className="text-emerald-400 text-sm font-medium mt-1">
                      ou R$ {(displayPrice * 0.9).toFixed(2).replace('.', ',')} no PIX (10% off)
                    </p>
                    {billingPeriod === 'yearly' && (
                      <span className="text-[10px] text-slate-500 block mt-1">Cobrado anualmente</span>
                    )}
                  </div>
                  <div className="space-y-3 mb-8 flex-1">
                    {plan.features_json.map((feature, i) => (
                      <PricingFeature key={i} text={feature} />
                    ))}
                  </div>
                  <a href={`/login?plan=${plan.id}`} className="w-full">
                    <Button className={`w-full rounded-xl h-12 ${isPremium ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-800/50 hover:text-purple-100 border border-purple-500/50' : 'bg-slate-800 text-white hover:bg-slate-700 border border-slate-700'}`}>
                      {isPremium ? 'Assinar Premium' : `Assinar ${plan.name}`}
                    </Button>
                  </a>
                  <a href="/login?trial=true" className="w-full block mt-3">
                    <Button variant="outline" className={`w-full rounded-xl h-12 bg-transparent text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-white`}>
                      Iniciar Teste Grátis (7 dias)
                    </Button>
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-slate-950 py-12 px-6 text-center text-slate-500 text-sm">
        <p className="max-w-2xl mx-auto mb-4 leading-relaxed">
          O AutoWash Pro é uma plataforma de gestão para estéticas automotivas. Utilizamos a autenticação segura do Google para identificar usuários, permitir o acesso ao sistema e proteger os dados operacionais do seu negócio.
        </p>
        <div className="flex justify-center gap-4 mb-6">
          <a href="/privacidade.html" className="text-blue-500 hover:text-blue-400 transition-colors">Política de Privacidade</a>
          <span className="text-slate-700">|</span>
          <a href="/termos.html" className="text-blue-500 hover:text-blue-400 transition-colors">Termos de Uso</a>
        </div>
        <p>© {new Date().getFullYear()} AutoWash Pro. Todos os direitos reservados.</p>
        <p className="mt-2">Sistema Premium para Estética Automotiva.</p>
      </footer>
    </div>
  );
}

// Components auxiliares da Landing Page
function SolutionItem({ title, desc }: { title: string, desc: string }) {
  return (
    <div>
      <h4 className="text-white font-semibold flex items-center gap-2 mb-1">
        <ChevronRight className="w-4 h-4 text-emerald-400" /> {title}
      </h4>
      <p className="text-slate-400 text-sm pl-6">{desc}</p>
    </div>
  );
}

function PricingFeature({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 text-slate-300 text-sm">
      <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" />
      <span>{text}</span>
    </div>
  );
}
